import { IconButton, Stack, Modal, Typography, Menu, MenuItem } from '@mui/material';
import { IotModalBody } from './IotModalBody';
import { ViewChangeButton } from './ViewChangeButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from 'src/components/icon/Icon';
import { Reel } from 'src/components/reel';
import { IotTourModalProps } from './types';
import { Badge } from '@mui/material';
import { EIoTProviders, TIoTWebhook } from 'src/components/menu-wrapper/components/iot-menu/types';
import { IotNotificationsActionButton } from './IotNotificationsActionButton';
import { ModalButton } from 'src/components/popup/styles';
import IconUnit from 'src/units/Icon';

export const IotModal = ({
  open,
  handleClose,
  tag,
  providers,
  setSelectedIndex,
  selectedCameras,
  handleCameraSelect,
  unshownDevices,
  moveToTag,
  handleUnlock,
  cameras,
  bottomBarWidth,
  windowWidth,
  handleOpenVideo,
  modalStyle,
  reelStyle,
  handleToggleFavourite,
  favouriteView,
  newDoorNotification,
  doors,
  handleMoveToLock,
  openVideoFromNotifications,
  removeCameraFromGrid,
  engines,
  things
}: IotTourModalProps) => {
  const [viewState, setViewState] = useState('band');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = useState<TIoTWebhook[]>([]);
  const isNotificationOpen = Boolean(anchorEl);

  const kisiProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.Kisi),
    [providers]
  );

  const tagHasDoors = useMemo(
    () => tag?.iotDevices?.some((device) => device.providerId === kisiProvider?.providerId),
    [tag, kisiProvider]
  );

  const handleClickNotificationIcon = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setNotifications((prev) => prev.map((data) => ({ ...data, seen: true })));
  };

  const handleCloseNotificationIcon = () => {
    setAnchorEl(null);
  };
  const handleViewChange = (type: string) => {
    setViewState(type);
  };

  useEffect(() => {
    if (newDoorNotification) {
      setNotifications((prev) =>
        prev.length < 4
          ? [newDoorNotification, ...prev]
          : [newDoorNotification, ...prev.slice(0, 3)]
      );
    }
  }, [newDoorNotification]);

  useEffect(() => {
    if (favouriteView) {
      setViewState('grid');
    }
  }, [favouriteView]);

  const handleShowCamera = useCallback(
    (n: TIoTWebhook) => {
      const foundDoor = doors?.find((d) => Number(d.externalDeviceId) === n.doorId);
      if (foundDoor) {
        const relatedCameras = cameras?.filter((c) =>
          c.relatedAssets?.find((a) => a.type === 'door' && a.id === foundDoor?.id)
        );

        if (relatedCameras && relatedCameras?.length > 0) {
          if (relatedCameras?.length > 1) {
            setViewState('grid');
          } else {
            setViewState('band');
          }
          handleCloseNotificationIcon();
          openVideoFromNotifications(relatedCameras);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doors, cameras]
  );

  const onClose = () => {
    handleClose();
    setViewState('band');
  };

  const handleMoveToDoor = (n: TIoTWebhook) => {
    const foundDoor = doors?.find((d) => Number(d.externalDeviceId) === n.doorId);
    if (foundDoor) {
      handleCloseNotificationIcon();
      onClose();
      handleMoveToLock(foundDoor);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        ...modalStyle,
        margin: 'auto',
        maxWidth: selectedCameras?.every((c) => !c.id && !c.url) ? '800px' : '100%',
        '@media (min-width: 801px)': {
          width: modalStyle?.left
            ? `calc(100% - ${modalStyle?.left as number}px)`
            : 'calc(100% - 80px)'
        },
        '@media (max-width: 800px)': {
          width: '100%',
          margin: '10px'
        },
        height: modalStyle?.top ? `calc(100% - ${modalStyle?.top})` : 'calc(100% - 80px)',
        color: '#fff',
        zIndex: 110,
        alignContent: 'center',
        '.MuiBackdrop-root': {
          background: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(5px)'
        }
      }}
    >
      <>
        <Stack
          width="100%"
          height={!!selectedCameras?.some((c) => c.id || c.url) ? '100%' : 'auto'}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden'
          }}
        >
          <Stack
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            pb={2}
            sx={{
              borderBottom: '1px solid #ffffff1f',
              padding: '10px',
              fontWeight: 'bold',
              fontSize: '18px',
              background: 'rgba(0, 0, 0, 0.5)'
            }}
          >
            <Typography sx={{ color: '#fff', ml: '10px' }}>{tag?.mpTitle && tag?.title}</Typography>
            {!!selectedCameras?.some((c) => c.id || c.url) && (
              <ViewChangeButton handleViewChange={handleViewChange} />
            )}

            <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
              {viewState === 'grid' && (
                <IconButton
                  onClick={handleToggleFavourite}
                  aria-label="close"
                  sx={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px',
                    color: '#fff',
                    background: 'rgba(0, 0, 0, 0.40)',
                    '&:hover': {
                      color: '#fff',
                      background: 'rgba(0, 0, 0, 0.40)'
                    }
                  }}
                >
                  <Icon icon={favouriteView ? 'fa-solid fa-star' : 'fa-regular fa-star'} />
                </IconButton>
              )}
              {tagHasDoors && (
                <IconButton onClick={handleClickNotificationIcon}>
                  <Badge
                    badgeContent={notifications?.filter((data) => !data.seen)?.length}
                    color="primary"
                  >
                    <Icon icon="fa-regular fa-bell" />
                  </Badge>
                </IconButton>
              )}

              <Menu
                anchorEl={anchorEl}
                open={isNotificationOpen}
                onClose={handleCloseNotificationIcon}
                sx={{ '.MuiPaper-root': { color: '#fff', background: 'rgba(0, 0, 0, 0.40)' } }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                {notifications?.map((n, i) => {
                  return (
                    <MenuItem key={i}>
                      {n.text}
                      <IotNotificationsActionButton
                        showCamera={() => handleShowCamera(n)}
                        moveToDoor={() => handleMoveToDoor(n)}
                      />
                    </MenuItem>
                  );
                })}
              </Menu>
              <ModalButton onClick={onClose}>
                <IconUnit.Close
                  style={{ color: 'white', width: '100%', height: '100%' }}
                  fontSize="large"
                  width={27}
                  height={27}
                />
              </ModalButton>
            </Stack>
          </Stack>
          <Stack
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: '80vh',
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.65)',

              '&::-webkit-scrollbar': {
                width: '6px'
              },

              '&::-webkit-scrollbar-thumb': {
                background: '#fff',
                borderRadius: '0px',
                width: '6px',
                height: '60px'
              }
            }}
          >
            <IotModalBody
              showDescription={false}
              tag={tag}
              providers={providers}
              setSelectedIndex={setSelectedIndex}
              selectedCameras={selectedCameras}
              handleCameraSelect={handleCameraSelect}
              unshownDevices={unshownDevices}
              viewState={viewState}
              moveToTag={(tag, fromGridView) => {
                onClose();
                moveToTag(tag, fromGridView);
              }}
              cameras={cameras}
              handleUnlock={handleUnlock}
              doors={doors}
              removeCameraFromGrid={removeCameraFromGrid}
              engines={engines}
              things={things}
            />
          </Stack>
        </Stack>
        {cameras &&
          cameras?.length > 1 &&
          viewState === 'band' &&
          !!selectedCameras?.some((c) => c.id || c.url) && (
            <Reel
              showReelIcon
              reelOpen
              showPlayIcon={false}
              reels={cameras}
              bottomBarWidth={bottomBarWidth}
              windowWidth={windowWidth}
              handleOpenVideo={handleOpenVideo}
              sx={reelStyle}
            />
          )}
      </>
    </Modal>
  );
};
